import type { Millimes, RenewalStatus } from '@beta-assur/shared';
import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Field } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TBody, TD, TR } from '../components/ui/table';
import { useApi } from '../lib/auth';
import { day, money, parseDinars, toDinarsInput } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';

// Proposition de renouvellement (BA-070) : le gestionnaire fixe la prime de la nouvelle période,
// l'assuré reçoit un message et répond depuis sa page ; la réponse arrive dans la file de travail.

interface RenewalOffer {
  id: string;
  premiumAmount: Millimes;
  startDate: string;
  endDate: string;
  validUntil: string;
  status: RenewalStatus;
  respondedAt: string | null;
}

const TONE = {
  pending: 'info',
  accepted: 'success',
  declined: 'warning',
  cancelled: 'neutral',
} as const;

export function RenewalCard({
  contractId,
  premium,
  expiryDate,
  editable,
}: {
  contractId: string;
  premium: Millimes;
  expiryDate: string;
  editable: boolean;
}) {
  const { t, language } = useI18n();
  const api = useApi();
  const path = `/v1/contracts/${contractId}/renewal-offers`;
  const query = useApiQuery<{ items: RenewalOffer[] }>(path);
  const [form, setForm] = useState({ premium: toDinarsInput(premium), validUntil: '' });
  const [error, setError] = useState<string | null>(null);
  const create = useApiMutation<Record<string, unknown>>('POST', path, { invalidate: [path] });
  const pending = query.data?.items.some((o) => o.status === 'pending') ?? false;
  // Contrat déjà échu : plus de renouvellement possible (refusé aussi par l'API).
  const expired = expiryDate < new Date().toISOString().slice(0, 10);

  function submit(event: FormEvent) {
    event.preventDefault();
    const premiumAmount = parseDinars(form.premium);
    if (premiumAmount === null) return setError(t.renewal.invalid);
    setError(null);
    create.mutate({ premiumAmount, ...(form.validUntil ? { validUntil: form.validUntil } : {}) });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.renewal.title}</CardTitle>
      </CardHeader>
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty>{t.renewal.none}</Empty>
          ) : (
            <Table>
              <TBody>
                {data.items.map((o) => (
                  <TR key={o.id}>
                    <TD className="whitespace-nowrap">
                      {day(o.startDate, language)} - {day(o.endDate, language)}
                    </TD>
                    <TD className="tabular-nums">{money(o.premiumAmount, language)}</TD>
                    <TD>
                      <Badge tone={TONE[o.status]}>{t.renewal.statuses[o.status]}</Badge>
                    </TD>
                    <TD className="text-muted-foreground">
                      {o.status === 'pending'
                        ? `${t.renewal.validUntil} ${day(o.validUntil, language)}`
                        : ''}
                    </TD>
                    <TD>
                      {editable && o.status === 'pending' ? (
                        <ConfirmButton
                          label={t.renewal.cancel}
                          title={t.renewal.cancel}
                          message={t.renewal.cancelMessage}
                          variant="destructive"
                          onConfirm={async () => {
                            await api.request('POST', `/v1/renewal-offers/${o.id}/cancel`);
                            await query.refetch();
                          }}
                        />
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
      {editable && !pending && !expired ? (
        <CardContent className="border-t border-border">
          <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
            <Field label={t.renewal.premium}>
              <Input
                inputMode="decimal"
                value={form.premium}
                onChange={(e) => setForm((f) => ({ ...f, premium: e.target.value }))}
              />
            </Field>
            <Field label={t.renewal.validUntil} hint={t.renewal.validUntilHint}>
              <Input
                type="date"
                value={form.validUntil}
                onChange={(e) => setForm((f) => ({ ...f, validUntil: e.target.value }))}
              />
            </Field>
            <Button type="submit" disabled={create.isPending}>
              {t.renewal.propose}
            </Button>
          </form>
          {error ? (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="mt-2">
            <ActionError error={create.error} />
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
}
